import { useState, useEffect, useMemo, useReducer, lazy, memo } from 'react';
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom';

const VIPGen = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  
  // รวม state ทั้งหมดไว้ด้านบน
  const [profile, setProfile] = useState(null);
  const [keywords, setKeywords] = useState('');
  const [settings, setSettings] = useState({
    quality: '1', 
    version: '6.1',
    styles: [],
    aspects: [],
    personalization: '',
    additional: '',
    parameters: {
      stylize: false,
      stylizeValue: '0',
      chaos: false,
      chaosValue: '0',
      noValue: '',
      repeatValue: '1'
    }
  });
  const [allPrompts, setAllPrompts] = useState([]);
  const [generatedPrompts, setGeneratedPrompts] = useState([]);
  const [displayedPrompts, setDisplayedPrompts] = useState([]);
  const [autoSave, setAutoSave] = useState(false);
  const [vipFeatures, setVipFeatures] = useState({
    copySpace: false,
    whiteBackground: false,
    prefix: '',
    suffix: ''
  });
  const [customAspect, setCustomAspect] = useState({ width: '', height: '' });
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  // คำนวณจำนวน prompts
  const calculatedPrompts = useMemo(() => {
    if (!keywords || !settings?.styles) return 0;
    
    const keywordsCount = keywords
      .split('\n')
      .filter(keyword => keyword.trim() !== '')
      .length;
    
    const stylesCount = settings.styles.length || 1;
    
    return keywordsCount * stylesCount;
  }, [keywords, settings?.styles]);

  // Effect สำหรับโหลดการตั้งค่า
  useEffect(() => {
    const loadSettings = () => {
      const savedSettings = localStorage.getItem('promptSettings');
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          if (parsedSettings) {
            // ตั้งค่าเริ่มต้นสำหรับค่าที่อาจเป็น undefined
            const defaultSettings = {
              styles: [], 
              aspects: [],
              parameters: {
                stylize: false,
                stylizeValue: '0',
                chaos: false,
                chaosValue: '0',
                noValue: '',
                repeatValue: '1'
              },
              quality: '1',
              version: '6.1',
              personalization: '',
              additional: '',
              vipFeatures: {
                copySpace: false,
                whiteBackground: false,
                prefix: '',
                suffix: ''
              }
            };

            // รวมค่าที่บันทึกไว้กับค่าเริ่มต้น
            const mergedSettings = {
              ...defaultSettings,
              ...parsedSettings,
              // รับประกันว่า parameters จะมีค่าเริ่มต้นที่ถูกต้อง
              parameters: {
                ...defaultSettings.parameters,
                ...(parsedSettings.parameters || {})
              }
            };

            // แยกการตั้งค่า VIP features
            const { vipFeatures: savedVipFeatures, ...otherSettings } = mergedSettings;
            
            setSettings(otherSettings);
            if (savedVipFeatures) {
              setVipFeatures(prev => ({
                ...prev,
                ...savedVipFeatures
              }));
            }
            setAutoSave(true);
          }
        } catch (error) {
          console.error('Error loading settings:', error);
          // ถ้าเกิด error ให้ใช้ค่าเริ่มต้น
          setSettings({
            quality: '1',
            version: '6.1',
            styles: [],
            aspects: [],
            personalization: '',
            additional: '',
            parameters: {
              stylize: false,
              stylizeValue: '0',
              chaos: false,
              chaosValue: '0',
              noValue: '',
              repeatValue: '1'
            }
          });
        }
      }
    };

    // โหลดการตั้งค่าหลังจากตรวจสอบ auth สำเร็จ
    if (profile) {
      loadSettings();
    }
  }, [profile]);

  // Effect สำหรับตรวจสอบสิทธิ์
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!user) {
          console.log('ไม่พบข้อมูลผู้ใช้');
          navigate('/vip');
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('เกิดข้อผิดพลาดในการดึงข้อมูลโปรไฟล์:', profileError);
          navigate('/vip');
          return;
        }

        if (!profileData) {
          console.log('ไม่พบข้อมูลโปรไฟล์');
          navigate('/vip');
          return;
        }

        const allowedMemberships = ['vip', 'admin', 'lifetime'];
        const hasMembership = allowedMemberships.includes(profileData.membership?.toLowerCase()) || 
                            (profileData.membership === 'free' && profileData.has_purchased_vip === true);

        if (!hasMembership) {
          console.log('ไม่มีสิทธิ์เข้าถึง VIP');
          navigate('/vip');
          return;
        }

        setProfile(profileData);

      } catch (error) {
        console.error('เกิดข้อผิดพลาดในการตรวจสอบสิทธิ์:', error);
        navigate('/vip');
      }
    };

    checkAuth();
  }, [user, navigate]);

  // แสดง loading ระหว่างตรวจสอบสิทธิ์
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const handleStyleChange = (style) => {
    const newStyles = settings.styles.includes(style)
      ? settings.styles.filter(s => s !== style)
      : [...settings.styles, style];
    setSettings({ ...settings, styles: newStyles });
  };

  const handleAspectChange = (aspect) => {
    const newAspects = settings.aspects.includes(aspect)
      ? settings.aspects.filter(a => a !== aspect)
      : [...settings.aspects, aspect];
    setSettings({ ...settings, aspects: newAspects });
  };

  const handleParameterChange = (param) => {
    setSettings({
      ...settings,
      parameters: {
        ...settings.parameters,
        [param]: !settings.parameters[param]
      }
    });
  };

  const handleParameterValueChange = (param, value) => {
    setSettings({
      ...settings,
      parameters: {
        ...settings.parameters,
        [`${param}Value`]: value
      }
    });
  };

  const handleGeneratePrompt = async () => {
    console.log('handleGeneratePrompt called');
    setIsLoading(true);
    try {
      if (!keywords.trim()) {
        console.log('No keywords');
        setNotification({
          show: true,
          message: 'กรุณาใส่ keywords อย่างน้อย 1 รายการ',
          type: 'error'
        });
        throw new Error('กรุณาใส่ keywords อย่างน้อย 1 รายการ');
      }

      // ไม่ต้องเช็ค style แล้ว
      console.log('Selected styles:', settings.styles);

      const keywordsList = keywords
        .split('\n')
        .map(k => k.trim())
        .filter(k => k.length > 0);

      if (keywordsList.length === 0) {
        throw new Error('กรุณาใส่ keywords ที่ถูกต้องอย่างน้อย 1 รายการ');
      }

      // ใช้ styles ที่เลือก หรือถ้าไม่มีก็ใช้ค่าว่าง
      const selectedStyles = settings.styles.length > 0 ? settings.styles : [''];
      let parameters = '';
      
      if (settings.parameters?.stylizeValue && settings.parameters.stylizeValue !== '0') {
        parameters += ` --stylize ${settings.parameters.stylizeValue}`;
      }

      if (settings.parameters?.chaosValue && settings.parameters.chaosValue !== '0') {
        parameters += ` --chaos ${settings.parameters.chaosValue}`;
      }

      if (settings.parameters?.noValue?.trim()) {
        parameters += ` --no ${settings.parameters.noValue.trim()}`;
      }

      if (settings.parameters?.repeatValue && parseInt(settings.parameters.repeatValue) > 1) {
        parameters += ` --repeat ${settings.parameters.repeatValue}`;
      }

      if (settings.additional?.trim()) {
        parameters += ` ${settings.additional.trim()}`;
      }

      let personalizationValues = [];
      if (settings.personalization?.trim()) {
        personalizationValues = settings.personalization
          .split('\n')
          .map(p => p.trim())
          .filter(p => p.length > 0);
      }    

      const prompts = [];
      keywordsList.forEach(keyword => {
        selectedStyles.forEach(style => {
          let basePrompt = '';
            
          if (vipFeatures.prefix?.trim()) {
            basePrompt += vipFeatures.prefix.trim() + ' ';
          }

          basePrompt += keyword.trim();
          
          if (style) {
            if (style === 'Realistic') {
              basePrompt += ', realistic, photography';
            } else {
              basePrompt += `, ${style.toLowerCase()}`;
            }
          }
            
          if (vipFeatures.suffix?.trim()) {
            basePrompt += ' ' + vipFeatures.suffix.trim();
          }

          if (vipFeatures.copySpace) {
            basePrompt += ', copy space for text';
          }
          
          if (vipFeatures.whiteBackground) {
            basePrompt += ', isolate on white background';
          }

          if (settings.aspects && settings.aspects.length > 0) {
            const selectedAspect = settings.aspects[Math.floor(Math.random() * settings.aspects.length)];
            if (selectedAspect) basePrompt += ` --ar ${selectedAspect}`;
          }
            
          if (settings.version === 'niji') {
            basePrompt += ' --niji 6';
          } else if (settings.version && settings.version.toLowerCase() !== 'none') {
            basePrompt += ` --v ${settings.version}`;
          }
            
          if (settings.quality && settings.quality.toLowerCase() !== 'none') {
            basePrompt += ` --q ${settings.quality}`;
          }

          if (parameters) {
            basePrompt += parameters;
          }

          if (personalizationValues.length > 0) {
            basePrompt += ` ${personalizationValues[Math.floor(Math.random() * personalizationValues.length)]}`;
          }

          prompts.push(basePrompt);
        });
      });

      if (prompts.length === 0) {
        throw new Error('ไม่สามารถสร้าง prompts ได้ กรุณาตรวจสอบการตั้งค่า');
      }

      setAllPrompts(prompts);
      setGeneratedPrompts(prompts);
      // แสดงทุก prompt ไม่จำกัดแค่ 5 รายการ
      setDisplayedPrompts(prompts);

      await incrementPromptCount();

      setNotification({
        show: true,
        message: `สร้าง ${prompts.length} prompts สำเร็จ`,
        type: 'success'
      });

    } catch (error) {
      setNotification({
        show: true,
        message: error.message || 'เกิดข้อผิดพลาดในการสร้าง prompts',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPrompts = async () => {
    if (allPrompts.length === 0) {
      setNotification({
        show: true,
        message: 'ไม่มี prompts ที่จะดาวน์โหลด',
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
      return;
    }

    try {
      const promptsText = allPrompts.join('\n\n');
      const fileName = generateFileName();
      const blob = new Blob([promptsText], { type: 'text/plain' });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      setNotification({
        show: true,
        message: 'ดาวน์โหลดไฟล์สำเร็จ',
        type: 'success'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);

    } catch (error) {
      setNotification({
        show: true,
        message: 'เกิดข้อผิดพลาดในการดาวน์โหลด: ' + error.message,
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotification({
        show: true,
        message: 'คัลอกสำเร็จ!',
        type: 'success'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    } catch (err) {
      setNotification({
        show: true,
        message: 'ไม่สามารถคัดลอกได้: ' + err,
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    }
  };

  const copyAllPrompts = () => {
    const promptsText = allPrompts.join('\n');
    navigator.clipboard.writeText(promptsText);
    setNotification({
      show: true,
      message: 'คัดลอกทั้งหมดแล้ว',
      type: 'success'
    });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const generateFileName = () => {
    const now = new Date();
    const date = now.toISOString().split('T')[0]; 
    const time = now.toTimeString().split(' ')[0].replace(/:/g, ''); 
    const randomNumber = Math.floor(10000 + Math.random() * 90000);
    return `prompts_${date}_${time}_${randomNumber}`;
  };

  const countKeywords = () => {
    return keywords
      .split('\n')
      .filter(keyword => keyword.trim() !== '')
      .length;
  };

  const calculateTotalPrompts = () => {
    const keywordsCount = keywords
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0)
      .length;

    const stylesCount = settings.styles.length || 1;
    return keywordsCount * stylesCount;
  };

  const handleReset = () => {
    localStorage.removeItem('promptSettings');
    setSettings({
      quality: '1',
      version: '6.1',
      styles: [],
      aspects: [],
      personalization: '',
      additional: '',
      parameters: {
        stylize: false,
        stylizeValue: '0',
        chaos: false,
        chaosValue: '0',
        noValue: '',
        repeatValue: '1'
      }
    });
  };

  const styleOptions = [
    { value: 'Realistic', label: 'Realistic' },
    { value: 'Illustrations', label: 'Illustrations' },
    { value: 'Watercolor', label: 'Watercolor' },
    { value: 'Anime', label: 'Anime' },
    { value: 'Digital Art', label: 'Digital Art' },
    { value: 'Oil Painting', label: 'Oil Painting' }
  ];

  const handleStyleToggle = (styleValue) => {
    setSettings(prev => {
      const newStyles = prev.styles.includes(styleValue)
        ? prev.styles.filter(s => s !== styleValue) 
        : [...prev.styles, styleValue]; 
      
      return {
        ...prev,
        styles: newStyles
      };
    });
  };

  const handleCustomAspect = () => {
    if (!customAspect.width || !customAspect.height) return;
    const aspectValue = `${customAspect.width}:${customAspect.height}`;
    handleAspectChange(aspectValue);
    setCustomAspect({ width: '', height: '' }); 
  };

  const handleSaveSettings = () => {
    try {
      const settingsToSave = {
        ...settings,
        vipFeatures
      };
      localStorage.setItem('promptSettings', JSON.stringify(settingsToSave));
      alert('บันทึกการตั้ง���่าเรียบร้อย');
    } catch (error) {
      alert('เกิดข้อผิดพ��าดในการบันทึกการตั้งค่า');
    }
  };

  const incrementPromptCount = async () => {
    try {
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('prompt_count')
        .eq('id', user.id)
        .single();

      const currentCount = currentProfile?.prompt_count || 0;
      const newPromptsCount = allPrompts.length;

      const { error } = await supabase
        .from('profiles')
        .update({ 
          prompt_count: currentCount + newPromptsCount 
        })
        .eq('id', user.id);

      if (error) throw error;

    } catch (error) {
    }
  };

  const updatePromptCount = async (userId, newCount) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ prompt_count: newCount })
        .eq('id', userId);
      
      if (error) throw error;
      return true;
    } catch (error) {
      return false;
    }
  };

  const updateDailyCount = async (currentCount) => {
    const today = new Date().toISOString().split('T')[0];
    
    const { error } = await supabase
      .from('daily_counts')
      .upsert({
        user_id: user.id,
        created_date: today,
        count: currentCount + 1
      }, {
        onConflict: 'user_id,created_date'
      });

    if (error) {
      return false;
    }

    return true;
  };

  const validateSetName = (name) => {
    return /^[a-zA-Z0-9\s-_]*$/.test(name);
  };

  const handleError = (error, message) => {
    setNotification({
      show: true,
      message: message || 'เกิดข้อผิดพลาด กรุณาลงหรือหน้าเ��็บเพื่อสร้าง prompts',
      type: 'error'
    });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const showNotification = (message, type = 'info') => {
    setNotification({
      show: true,
      message,
      type
    });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Prompt Generator</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto p-6">
          
          <div className="mb-8 bg-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-red-300">Keywords</h2>  
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-400">พิมพ์ Keywords (แต่ละบรรทัดคือ 1 keyword)</label>
              <textarea
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="พิมพ์ keywords แต่ละบรรทัด"
                className="w-full p-4 bg-black/40 border border-gray-700 rounded-lg text-gray-300 h-40 focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          <div className="mb-8 bg-gradient-to-br from-purple-900/50 to-blue-900/50 rounded-lg p-6 border border-purple-700/50">
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center gap-12 flex-wrap">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">
                    {countKeywords()}
                  </div>
                  <div className="text-sm text-gray-400">Keywords</div>
                </div>

                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">
                    {calculateTotalPrompts()}
                  </div>
                  <div className="text-sm text-gray-400">Prompt</div>
                </div>

                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">
                    {calculateTotalPrompts() * 4}
                  </div>
                  <div className="text-sm text-gray-400">รูป</div>
                </div>
              </div>
            </div>
          </div>

          <button 
            onClick={() => {
              console.log('Button clicked');
              handleGeneratePrompt();
            }}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-semibold text-lg shadow-xl hover:shadow-purple-500/20 hover:scale-[1.02] backdrop-blur z-10 relative"
            disabled={isLoading}
          >
            {isLoading ? 'Generating...' : 'สร้าง Prompt'}
          </button>

          {displayedPrompts.length > 0 && (
            <div className="mt-8 bg-gray-900 rounded-lg p-6 border border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-200">ผลลัพธ์ (ทั้งหมด {displayedPrompts.length} prompts)</h2>
                <div className="flex gap-2">
                  <button
                    onClick={copyAllPrompts}
                    className="flex items-center gap-1 bg-purple-600 text-white px-4 py-1.5 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    คัดลอกทั้งหมด
                  </button>
                  <button
                    onClick={downloadPrompts}
                    className="flex items-center gap-1 bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    ดาวน์โหลด .txt
                  </button>
                </div>
              </div>
              
              {/* แสดงผลลัพธ์ตัวอย่าง */}
              <div className="bg-gray-900 p-4 rounded-lg font-mono text-sm text-gray-300 flex justify-between items-center min-h-[40px] mb-4">
                <div>{displayedPrompts[0] || 'ยังไม่มีผลลัพธ์'}</div>
              </div>
              
              {/* แสดงผลลัพธ์ทั้งหมด */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {displayedPrompts.map((prompt, index) => (
                  <div key={index} className="group relative p-4 bg-black/40 rounded-lg hover:bg-black/60 transition-colors">
                    <p className="text-gray-300 font-mono break-all pr-10">{prompt}</p>
                    <button
                      onClick={() => copyToClipboard(prompt)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-white transition-colors"
                      title="คัดลอกไปยังคลิปบอร์ด"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8 bg-gray-900/90 rounded-lg p-6 border border-gray-800">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
              {styleOptions.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleStyleChange(value)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                    settings.styles.includes(value)
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/20'
                      : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-purple-500/50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-8 bg-gray-900/90 rounded-lg p-6 border border-gray-800">
            
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-3">
              {[
                { value: '1:1', label: 'Square 1:1' },
                { value: '16:9', label: 'Landscape 16:9' },
                { value: '9:16', label: 'Portrait 9:16' },
                { value: '4:3', label: 'Standard 4:3' },
                { value: '3:4', label: 'Portrait 3:4' },
                { value: '2:1', label: 'Cinematic 2:1' },
                { value: '1:2', label: 'Vertical 1:2' },
                { value: '3:2', label: 'Classic 3:2' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleAspectChange(value)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 hover:scale-105 ${
                    settings.aspects.includes(value)
                      ? 'bg-purple-500/20 border-purple-500 text-purple-400 shadow-lg shadow-purple-500/20'
                      : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-purple-500/50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-8 relative">
            <div className="p-6 bg-gradient-to-br from-yellow-900/30 via-purple-900/30 to-pink-900/30 rounded-xl border border-yellow-500/20 shadow-2xl backdrop-blur-sm relative overflow-hidden">
              
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-0 left-0 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl"></div>
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-2xl">👑</span>
                  <h3 className="text-xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                    VIP Features
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative z-10">
                  <button
                    onClick={() => {
                      setAutoSave(!autoSave);
                      if (!autoSave) {
                        try {
                          const settingsToSave = {
                            ...settings,
                            vipFeatures
                          };
                          localStorage.setItem('promptSettings', JSON.stringify(settingsToSave));
                          showNotification('บันทึกการต้งค่าเรียบร้อย');
                        } catch (error) {
                          showNotification('เกิด้อผิดพลาในการบันทึกการตั้งค่า', 'error');
                        }
                      } else {
                        localStorage.removeItem('promptSettings');
                        showNotification('��กเลิกกาบันทึกกรตั้งค่าแล้ว', 'info');
                      }
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: '' });
                      }, 3000);
                    }}
                    className={`px-4 py-2 rounded-lg border ${
                      autoSave
                        ? 'bg-green-500/20 border-green-500 text-green-400'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {autoSave ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        )}
                      </svg>
                      <span>{autoSave ? 'กำลังบันทึกอัตโนมัติ' : 'บันทึกการตั้งค่า'}</span>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setVipFeatures(prev => ({
                        ...prev,
                        copySpace: !prev.copySpace
                      }));
                    }}
                    className={`px-4 py-2 rounded-lg border ${
                      vipFeatures.copySpace
                        ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Copy Space
                  </button>

                  <button
                    onClick={() => {
                      setVipFeatures(prev => ({
                        ...prev,
                        whiteBackground: !prev.whiteBackground
                      }));
                    }}
                    className={`px-4 py-2 rounded-lg border ${
                      vipFeatures.whiteBackground
                        ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    White Background
                  </button>

                  <button
                    onClick={() => {
                      setSettings(prev => ({
                        ...prev,
                        parameters: {
                          ...prev.parameters,
                          personalization: !prev.parameters.personalization
                        }
                      }));
                    }}
                    className={`px-4 py-2 rounded-lg border ${
                      settings.parameters.personalization
                        ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Personalization
                  </button>
                </div>

                <div className="flex items-center gap-4 mt-4">
                  <h3 className="text-lg font-medium text-yellow-400 whitespace-nowrap">Custom Aspect Ratio:</h3>
                  <div className="flex items-center gap-4">
                    <div>
                      <input
                        type="number"
                        value={customAspect.width}
                        onChange={(e) => setCustomAspect(prev => ({ ...prev, width: e.target.value }))}
                        min="1"
                        placeholder="Width"
                        className="w-24 px-3 py-2 bg-black/40 border border-yellow-500/30 rounded text-center text-yellow-400 placeholder-yellow-400/50"
                      />
                    </div>
                    <span className="text-yellow-400">:</span>
                    <div>
                      <input
                        type="number"
                        value={customAspect.height}
                        onChange={(e) => setCustomAspect(prev => ({ ...prev, height: e.target.value }))}
                        min="1"
                        placeholder="Height"
                        className="w-24 px-3 py-2 bg-black/40 border border-yellow-500/30 rounded text-center text-yellow-400 placeholder-yellow-400/50"
                      />
                    </div>
                    <button
                      onClick={handleCustomAspect}
                      disabled={!customAspect.width || !customAspect.height}
                      className="px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 border border-yellow-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      เพิ่ม
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 font-medium text-yellow-400">Prompt Prefix</label>
                    <input
                      type="text"
                      value={vipFeatures.prefix}
                      onChange={(e) => setVipFeatures(prev => ({
                        ...prev,
                        prefix: e.target.value
                      }))}
                      placeholder="ข้อความที่จะใส่กอน keyword"
                      className="w-full p-2 bg-black/40 border border-yellow-500/30 rounded-lg text-yellow-400 placeholder-yellow-400/50 focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-yellow-400">Prompt Suffix</label>
                    <input
                      type="text"
                      value={vipFeatures.suffix}
                      onChange={(e) => setVipFeatures(prev => ({
                        ...prev,
                        suffix: e.target.value
                      }))}
                      placeholder="ข้อความที่ะะใส่หลัง keyword"
                      className="w-full p-2 bg-black/40 border border-yellow-500/30 rounded-lg text-yellow-400 placeholder-yellow-400/50 focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-medium text-yellow-400">Additional Parameters</label>
                  <input
                    type="text"
                    value={settings.additional}
                    onChange={(e) => setSettings({...settings, additional: e.target.value})}
                    placeholder="เพิ่มพารามิเตร์อื่นๆ เช่น --s 750 --c 10 (ไม่จำเป็น)"
                    className="w-full p-2 bg-black/40 border border-yellow-500/30 rounded-lg text-yellow-400 placeholder-yellow-400/50 focus:ring-2 focus:ring-yellow-500"
                  />
                </div>

                {settings.parameters.personalization && (
                  <div>
                    <textarea
                      value={settings.personalization}
                      onChange={(e) => setSettings({
                        ...settings,
                        personalization: e.target.value
                      })}
                      placeholder="ใส่ค่า Personalization (บรรทัดละค่า)"
                      className="w-full p-2 bg-black/40 border border-yellow-500/30 rounded-lg text-yellow-400 placeholder-yellow-400/50 focus:ring-2 focus:ring-yellow-500 min-h-[100px]"
                    />
                  </div>
                )}

                <div className="grid grid-cols-4 gap-4 mt-4">
                  <div className="bg-black/40 p-4 rounded-lg border border-yellow-500/30">
                    <label className="block text-sm text-yellow-400/70 mb-2">Stylize</label>
                    <select
                      value={settings.parameters.stylizeValue}
                      onChange={(e) => setSettings({
                        ...settings,
                        parameters: {
                          ...settings.parameters,
                          stylizeValue: e.target.value
                        }
                      })}
                      className="w-full p-2.5 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-lg text-yellow-300 
                        focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 
                        outline-none appearance-none cursor-pointer
                        hover:border-yellow-400/50 hover:bg-black/60 transition-all duration-200
                        [&>option]:bg-gray-900 [&>option]:text-yellow-300"
                    >
                      <option value="0" className="p-2">ไม่มี Stylize (0)</option>
                      <option value="100" className="p-2">ค่าเริ่มต้น (100)</option>
                      <option value="250" className="p-2">ปานกลาง (250)</option>
                      <option value="500" className="p-2">สูง (500)</option>
                      <option value="750" className="p-2">สูงมาก (750)</option>
                    </select>
                  </div>

                  <div className="bg-black/40 p-4 rounded-lg border border-yellow-500/30">
                    <label className="block text-sm text-yellow-400/70 mb-2">Chaos</label>
                    <select
                      value={settings.parameters.chaosValue}
                      onChange={(e) => setSettings({
                        ...settings,
                        parameters: {
                          ...settings.parameters,
                          chaosValue: e.target.value
                        }
                      })}
                      className="w-full p-2.5 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-lg text-yellow-300 
                        focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 
                        outline-none appearance-none cursor-pointer
                        hover:border-yellow-400/50 hover:bg-black/60 transition-all duration-200
                        [&>option]:bg-gray-900 [&>option]:text-yellow-300"
                    >
                      <option value="0" className="p-2">ไม่มี Chaos (0)</option>
                      <option value="10" className="p-2">ต่ำ (10)</option>
                      <option value="20" className="p-2">ปานกลาง (20)</option>
                      <option value="50" className="p-2">สูง (50)</option>
                      <option value="80" className="p-2">สูงมาก (80)</option>
                    </select>
                  </div>

                  <div className="bg-black/40 p-4 rounded-lg border border-yellow-500/30">
                    <label className="block text-sm text-yellow-400/70 mb-2">No</label>
                    <input
                      type="text"
                      value={settings.parameters.noValue}
                      onChange={(e) => setSettings({
                        ...settings,
                        parameters: {
                          ...settings.parameters,
                          noValue: e.target.value
                        }
                      })}
                      placeholder="ใส่คำที่ไม่ต้องการ"
                      className="w-full p-2 bg-black/40 border border-yellow-500/30 rounded text-yellow-400 focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>

                  <div className="bg-black/40 p-4 rounded-lg border border-yellow-500/30">
                    <label className="block text-sm text-yellow-400/70 mb-2">Repeat</label>
                    <input
                      type="number"
                      value={settings.parameters.repeatValue}
                      onChange={(e) => setSettings({
                        ...settings,
                        parameters: {
                          ...settings.parameters,
                          repeatValue: e.target.value
                        }
                      })}
                      min="1"
                      placeholder="จำนวนรอบ"
                      className="w-full p-2 bg-black/40 border border-yellow-500/30 rounded text-yellow-400 focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8 bg-gray-900/90 rounded-lg p-6 border border-gray-800">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              <div className="bg-black/40 p-4 rounded-lg border border-green-500/30">
                <label className="block text-sm text-green-400/70 mb-2">Quality</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: '2', label: 'High' },
                    { value: '1', label: 'Medium' },
                    { value: '.5', label: 'Low' },
                    { value: 'none', label: 'None' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setSettings(prev => ({ ...prev, quality: value }))}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                        settings.quality === value
                          ? 'bg-green-500/20 border-green-500 text-green-400 shadow-lg shadow-green-500/20'
                          : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-green-500/50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-black/40 p-4 rounded-lg border border-green-500/30">
                <label className="block text-sm text-green-400/70 mb-2">Version</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: '7', label: 'V7' },
                    { value: '6.1', label: 'V6.1' },
                    { value: '5.2', label: 'V5.2' },
                    { value: 'niji', label: 'Niji' },
                    { value: 'none', label: 'None' }
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setSettings(prev => ({ ...prev, version: value }))}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                        settings.version === value
                          ? 'bg-green-500/20 border-green-500 text-green-400 shadow-lg shadow-green-500/20'
                          : 'bg-black/40 border-gray-800 text-gray-400 hover:bg-gray-800/80 hover:border-green-500/50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

VIPGen.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

export default VIPGen;